<template>
  <div class="home">
    <div class="board-header">
      <div>
        <h1
          ref="boardTitleHeading"
          class="board-title"
          :class="{ 'is-read-only': !currentBoardCanEdit }"
          :contenteditable="currentBoardCanEdit ? 'true' : 'false'"
          spellcheck="false"
          :tabindex="currentBoardCanEdit ? 0 : -1"
          @blur="saveBoardTitle"
          @input="editableBoardTitle = $event.target.innerText"
          @keydown.enter.prevent="$event.target.blur()"
          @keydown.esc.prevent="cancelBoardTitleEdit"
        >
          {{ currentBoardTitle }}
        </h1>
        <p class="text-muted mb-0">Workflow Shortcuts</p>
      </div>
      <div class="board-header-actions">
        <b-button
          v-if="isSignedIn"
          class="btn-green"
          size="sm"
          :disabled="isCreatingBoard"
          @click="createBoard"
        >
          New Board
        </b-button>
        <b-button
          :class="addColumnButtonClass"
          size="sm"
          :disabled="!currentBoardCanEdit"
          @click="addColumn"
        >
          Add Column
        </b-button>
        <b-button class="btn-green-outline" size="sm" @click="loadDocuments">
          Refresh
        </b-button>
      </div>
    </div>

    <div v-if="error" class="app-notice app-notice-error" role="status">
      {{ error }}
    </div>

    <div v-if="readOnlyNotice" class="app-notice app-notice-info" role="status">
      {{ readOnlyNotice }}
    </div>

    <section class="compose-panel" :class="{ 'is-read-only': !currentBoardCanEdit }">
      <b-form-input
        v-model="newDocumentTitle"
        placeholder="Document title"
        :disabled="!currentBoardCanEdit"
        @keyup.enter="createDocument"
      />
      <div class="editor-shell">
        <div class="editor-toolbar" aria-label="Document notes formatting">
          <button
            type="button"
            :disabled="!currentBoardCanEdit"
            @mousedown.prevent="formatNewDocument('bold')"
          >
            <strong>B</strong>
          </button>
          <button
            type="button"
            :disabled="!currentBoardCanEdit"
            @mousedown.prevent="formatNewDocument('italic')"
          >
            <em>I</em>
          </button>
          <button
            type="button"
            :disabled="!currentBoardCanEdit"
            @mousedown.prevent="formatNewDocument('insertUnorderedList')"
          >
            •
          </button>
          <button
            type="button"
            :disabled="!currentBoardCanEdit"
            @mousedown.prevent="formatNewDocument('insertOrderedList')"
          >
            1.
          </button>
        </div>
        <div
          ref="newDocumentEditor"
          class="rich-editor"
          :contenteditable="currentBoardCanEdit ? 'true' : 'false'"
          :aria-readonly="!currentBoardCanEdit"
          data-placeholder="Document notes"
          @input="syncNewDocumentText"
        ></div>
      </div>
      <b-button
        :class="addDocumentButtonClass"
        :disabled="isSaving || !currentBoardCanEdit"
        @click="createDocument"
      >
        Add Document
      </b-button>
    </section>

    <div v-if="isLoading" class="text-muted py-4">Loading board...</div>

    <div v-else class="kanban-bleed">
      <draggable
        v-model="columns"
        class="kanban-board"
        :style="kanbanGridStyle"
        handle=".column-drag-handle"
        :disabled="!currentBoardCanEdit"
        @end="persistBoardColumns"
      >
      <section
        class="kanban-column"
        v-for="column in columns"
        :key="column.key"
      >
        <header class="kanban-column-header">
          <button
            class="column-drag-handle"
            type="button"
            aria-label="Move column"
            title="Move column"
            :disabled="!currentBoardCanEdit"
          >
            ::
          </button>
          <h2
            class="column-title"
            :class="{ 'is-read-only': !currentBoardCanEdit }"
            :contenteditable="currentBoardCanEdit ? 'true' : 'false'"
            spellcheck="false"
            :tabindex="currentBoardCanEdit ? 0 : -1"
            @blur="saveColumnTitle(column, $event)"
            @keydown.enter.prevent="$event.target.blur()"
            @keydown.esc.prevent="resetColumnTitle(column, $event)"
          >
            {{ column.title }}
          </h2>
          <div class="column-controls">
            <span>{{ documents[column.key].length }}</span>
            <button
              v-if="columns.length > 1 && currentBoardCanEdit"
              class="column-remove"
              type="button"
              aria-label="Remove column"
              @click="removeColumn(column)"
            >
              ×
            </button>
          </div>
        </header>

        <draggable
          class="kanban-list"
          :list="documents[column.key]"
          group="documents"
          :disabled="!currentBoardCanEdit"
          @change="persistStatuses"
        >
          <article
            class="document-card"
            v-for="document in documents[column.key]"
            :key="document._id || document.title"
            role="button"
            tabindex="0"
            @click="openDocumentModal(document)"
            @keydown.enter="openDocumentModal(document)"
          >
            <h3 class="document-title">{{ document.title || 'Untitled document' }}</h3>
          </article>
        </draggable>
      </section>
      </draggable>
    </div>

    <b-modal
      v-model="isDocumentModalOpen"
      size="lg"
      :title="documentModalTitle"
      hide-footer
      @hidden="resetDocumentModal"
    >
      <div class="document-modal-body">
        <label class="modal-label" for="modal-document-title">Title</label>
        <b-form-input
          id="modal-document-title"
          v-model="modalDocumentTitle"
          class="modal-title-input"
          :disabled="!currentBoardCanEdit"
        />

        <label class="modal-label">Document notes</label>
        <div class="editor-shell modal-editor-shell">
          <div class="editor-toolbar" aria-label="Document formatting">
            <button
              type="button"
              :disabled="!currentBoardCanEdit"
              @mousedown.prevent="formatModalDocument('bold')"
            >
              <strong>B</strong>
            </button>
            <button
              type="button"
              :disabled="!currentBoardCanEdit"
              @mousedown.prevent="formatModalDocument('italic')"
            >
              <em>I</em>
            </button>
            <button
              type="button"
              :disabled="!currentBoardCanEdit"
              @mousedown.prevent="formatModalDocument('insertUnorderedList')"
            >
              •
            </button>
            <button
              type="button"
              :disabled="!currentBoardCanEdit"
              @mousedown.prevent="formatModalDocument('insertOrderedList')"
            >
              1.
            </button>
          </div>
          <div
            ref="modalDocumentEditor"
            class="rich-editor modal-rich-editor"
            :contenteditable="currentBoardCanEdit ? 'true' : 'false'"
            :aria-readonly="!currentBoardCanEdit"
            data-placeholder="Document notes"
            @input="syncModalDocumentText"
          ></div>
        </div>

        <div class="modal-actions">
          <b-button
            v-if="currentBoardCanEdit"
            variant="outline-danger"
            :disabled="isModalSaving"
            @click="deleteSelectedDocument"
          >
            Delete
          </b-button>
          <div class="modal-actions-right">
            <b-button
              class="btn-green-outline"
              :disabled="isModalSaving"
              @click="isDocumentModalOpen = false"
            >
              Close
            </b-button>
            <b-button
              v-if="currentBoardCanEdit"
              class="btn-green"
              :disabled="isModalSaving"
              @click="saveDocumentModal"
            >
              Save
            </b-button>
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import draggable from 'vuedraggable';
import { getCurrentUser } from '../services/authService';
import { createResourceService, getErrorMessage } from '../services/resourceService';

const documentsService = createResourceService('documents');
const boardsService = createResourceService('boards');
const currentBoardIdStorageKey = 'workflowshortcuts.currentBoardId';
const currentBoardTitleStorageKey = 'workflowshortcuts.currentBoardTitle';
const currentBoardIncludesLegacyDocumentsStorageKey =
  'workflowshortcuts.currentBoardIncludesLegacyDocuments';
const boardColumnsStorageKey = 'workflowshortcuts.boardColumns';
const legacyBoardColumnsStorageKey = boardColumnsStorageKey;
const selectedBoardStorageKey = 'workflowshortcuts.selectedBoard';
const anonymousPlaygroundBoardIdStorageKey = 'workflowshortcuts.playgroundBoardId';
const defaultColumns = [
  { key: 'backlog', title: 'Backlog' },
  { key: 'drafting', title: 'Drafting' },
  { key: 'review', title: 'Review' },
  { key: 'published', title: 'Published' },
];

function cloneDefaultColumns() {
  return defaultColumns.map((column) => ({ ...column }));
}

function getBoardColumnsStorageKey(boardId = '') {
  return boardId ? `${boardColumnsStorageKey}.${boardId}` : legacyBoardColumnsStorageKey;
}

function normalizeColumns(columns) {
  if (!Array.isArray(columns) || !columns.length) {
    return null;
  }

  const normalizedColumns = columns
    .filter((column) => column && column.key && column.title)
    .map((column) => ({ key: column.key, title: column.title }));

  return normalizedColumns.length ? normalizedColumns : null;
}

function getSelectedBoardFromStorage(boardId) {
  try {
    const board = JSON.parse(window.localStorage.getItem(selectedBoardStorageKey));

    if (board && String(board._id) === String(boardId)) {
      return board;
    }
  } catch (error) {
    return null;
  }

  return null;
}

function isValidBoard(board, boardId) {
  return board && typeof board === 'object' && String(board._id) === String(boardId);
}

function writeSelectedBoardToStorage(board) {
  if (!board || !board._id) {
    return;
  }

  window.localStorage.setItem(selectedBoardStorageKey, JSON.stringify(board));
}

function emptyBoard(columns = defaultColumns) {
  return columns.reduce((board, column) => {
    board[column.key] = [];
    return board;
  }, {});
}

function createColumnKey() {
  return `column-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function escapeHtml(text = '') {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function plainTextToHtml(text = '') {
  if (/<[a-z][\s\S]*>/i.test(text)) {
    return text;
  }

  return text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => `<p>${escapeHtml(line)}</p>`)
    .join('');
}

export default {
  name: 'HomeView',
  components: {
    draggable,
  },
  data() {
    return {
      columns: [
        { key: 'backlog', title: 'Backlog' },
        { key: 'drafting', title: 'Drafting' },
        { key: 'review', title: 'Review' },
        { key: 'published', title: 'Published' },
      ],
      currentBoardId: '',
      currentBoardTitle: 'Document Board',
      currentBoardCanEdit: true,
      currentBoardIncludesLegacyDocuments: true,
      currentUser: null,
      documents: emptyBoard(defaultColumns),
      editableBoardTitle: 'Document Board',
      error: '',
      isLoading: false,
      isCreatingBoard: false,
      isSaving: false,
      isDocumentModalOpen: false,
      isModalSaving: false,
      modalDocumentText: '',
      modalDocumentTitle: '',
      newDocumentTitle: '',
      newDocumentText: '',
      selectedDocument: null,
    };
  },
  computed: {
    kanbanGridStyle() {
      return {
        gridTemplateColumns: `repeat(${this.columns.length}, minmax(240px, 1fr))`,
      };
    },
    documentModalTitle() {
      return this.currentBoardCanEdit ? 'Edit Document' : 'View Document';
    },
    isSignedIn() {
      return Boolean(this.currentUser);
    },
    addDocumentButtonClass() {
      return {
        'btn-green': true,
        'add-card-primary': !this.isSignedIn,
      };
    },
    addColumnButtonClass() {
      return this.isSignedIn ? 'btn-green-outline' : 'btn-green';
    },
    readOnlyNotice() {
      if (!this.currentBoardId || this.currentBoardCanEdit) {
        return '';
      }

      return 'Only the general public board is editable while logged out. Sign in to edit this public board.';
    },
  },
  created() {
    this.initializeBoard();
  },
  mounted() {
    window.addEventListener('workflowshortcuts-auth-changed', this.initializeBoard);
  },
  beforeDestroy() {
    window.removeEventListener('workflowshortcuts-auth-changed', this.initializeBoard);
  },
  watch: {
    '$route.query.boardId'(boardId) {
      if (boardId && boardId !== this.currentBoardId) {
        this.openBoardById(boardId);
      }
    },
  },
  methods: {
    async initializeBoard() {
      const routeBoardId = this.$route.query.boardId;
      const currentUser = await getCurrentUser();

      this.currentUser = currentUser;

      if (routeBoardId) {
        await this.openBoardById(routeBoardId);
        return;
      }

      if (!currentUser) {
        await this.ensureAnonymousPlayground();
        return;
      }

      const board = await this.getSignedInCurrentBoard();

      if (board) {
        this.rememberCurrentBoard(board, {
          includeLegacyDocuments: board.includeLegacyDocuments === true,
        });
        this.loadBoardColumns(board.columns);
        await this.loadDocuments();
        return;
      }

      this.loadCurrentBoard();
      this.loadBoardColumns();
      this.loadDocuments();
    },

    async getSignedInCurrentBoard() {
      const currentBoardId = window.localStorage.getItem(currentBoardIdStorageKey) || '';

      if (currentBoardId) {
        try {
          const board = await boardsService.get(currentBoardId);

          if (isValidBoard(board, currentBoardId)) {
            return board;
          }
        } catch (error) {
          window.localStorage.removeItem(currentBoardIdStorageKey);
          window.localStorage.removeItem(currentBoardTitleStorageKey);
          window.localStorage.removeItem(currentBoardIncludesLegacyDocumentsStorageKey);
        }
      }

      const boards = await boardsService.list();
      const ownedBoards = boards.filter((board) => board.canManage === true);
      const publicBoards = boards.filter((board) => board.visibility === 'public');

      if (ownedBoards.length) {
        return ownedBoards[0];
      }

      return publicBoards[0] || null;
    },

    async ensureAnonymousPlayground() {
      this.isLoading = true;
      this.error = '';

      try {
        const savedPlaygroundId =
          window.localStorage.getItem(anonymousPlaygroundBoardIdStorageKey) || '';
        let board = null;

        if (savedPlaygroundId) {
          try {
            board = await boardsService.get(savedPlaygroundId);
          } catch (error) {
            window.localStorage.removeItem(anonymousPlaygroundBoardIdStorageKey);
          }
        }

        if (!isValidBoard(board, savedPlaygroundId)) {
          const columns = cloneDefaultColumns();
          board = await boardsService.create({
            title: 'My Playground',
            text: 'Saved logged-out workspace.',
            status: 'active',
            type: 'board',
            visibility: 'private',
            columns,
            includeLegacyDocuments: false,
            isPlayground: true,
          });
          window.localStorage.setItem(anonymousPlaygroundBoardIdStorageKey, String(board._id));
        }

        this.rememberCurrentBoard(board, {
          includeLegacyDocuments: board.includeLegacyDocuments === true,
        });
        this.loadBoardColumns(board.columns);
        await this.loadDocuments();
      } catch (error) {
        this.error = getErrorMessage(error);
      } finally {
        this.isLoading = false;
      }
    },

    loadCurrentBoard() {
      this.currentBoardId = window.localStorage.getItem(currentBoardIdStorageKey) || '';
      this.currentBoardTitle =
        window.localStorage.getItem(currentBoardTitleStorageKey) || 'Document Board';
      this.currentBoardIncludesLegacyDocuments =
        window.localStorage.getItem(currentBoardIncludesLegacyDocumentsStorageKey) !== 'false';
      const storedBoard = getSelectedBoardFromStorage(this.currentBoardId);
      this.currentBoardCanEdit = storedBoard ? storedBoard.canEdit !== false : true;
      this.editableBoardTitle = this.currentBoardTitle;
      this.syncBoardTitleHeading();
    },

    rememberCurrentBoard(board, options = {}) {
      const includeLegacyDocuments =
        options.includeLegacyDocuments !== undefined
          ? options.includeLegacyDocuments
          : this.currentBoardIncludesLegacyDocuments;

      this.currentBoardId = String(board._id);
      this.currentBoardTitle = board.title || 'Document Board';
      this.currentBoardCanEdit = board.canEdit !== false;
      this.currentBoardIncludesLegacyDocuments = includeLegacyDocuments;
      this.editableBoardTitle = this.currentBoardTitle;
      window.localStorage.setItem(currentBoardIdStorageKey, this.currentBoardId);
      window.localStorage.setItem(currentBoardTitleStorageKey, this.currentBoardTitle);
      window.localStorage.setItem(
        currentBoardIncludesLegacyDocumentsStorageKey,
        String(this.currentBoardIncludesLegacyDocuments)
      );
      writeSelectedBoardToStorage({
        ...board,
        includeLegacyDocuments: this.currentBoardIncludesLegacyDocuments,
      });
      this.syncBoardTitleHeading();
    },

    ensureBoardEditable() {
      if (this.currentBoardCanEdit) {
        return true;
      }

      this.error = 'This board is read-only for your current session.';
      return false;
    },

    async openBoardById(boardId) {
      this.isLoading = true;
      this.error = '';

      try {
        let board = null;

        try {
          board = await boardsService.get(boardId);
        } catch (error) {
          board = getSelectedBoardFromStorage(boardId);
        }

        if (!isValidBoard(board, boardId)) {
          throw new Error('Board could not be opened. Refresh the boards list and try again.');
        }

        this.rememberCurrentBoard(board, {
          includeLegacyDocuments: board.includeLegacyDocuments === true,
        });
        this.loadBoardColumns(board.columns);
        await this.loadDocuments();
      } catch (error) {
        this.error = getErrorMessage(error);
      } finally {
        this.isLoading = false;
      }
    },

    syncBoardTitleHeading() {
      this.$nextTick(() => {
        if (this.$refs.boardTitleHeading) {
          this.$refs.boardTitleHeading.innerText = this.currentBoardTitle;
        }
      });
    },

    loadBoardColumns(boardColumns = null) {
      try {
        const storageKey = getBoardColumnsStorageKey(this.currentBoardId);
        const savedColumns = this.currentBoardId
          ? normalizeColumns(boardColumns) ||
            normalizeColumns(JSON.parse(window.localStorage.getItem(storageKey)))
          : normalizeColumns(JSON.parse(window.localStorage.getItem(storageKey))) ||
            normalizeColumns(JSON.parse(window.localStorage.getItem(legacyBoardColumnsStorageKey)));

        if (savedColumns) {
          this.columns = savedColumns;
        } else {
          this.columns = cloneDefaultColumns();
        }
      } catch (error) {
        this.columns = cloneDefaultColumns();
      }

      this.documents = emptyBoard(this.columns);
    },

    async persistBoardColumns() {
      if (!this.ensureBoardEditable()) {
        return;
      }

      const columns = this.columns.map((column) => ({ key: column.key, title: column.title }));
      window.localStorage.setItem(
        getBoardColumnsStorageKey(this.currentBoardId),
        JSON.stringify(columns)
      );

      if (!this.currentBoardId) {
        return;
      }

      try {
        const board = await boardsService.update(this.currentBoardId, {
          columns,
          includeLegacyDocuments: this.currentBoardIncludesLegacyDocuments,
        });
        this.rememberCurrentBoard(board, {
          includeLegacyDocuments: board.includeLegacyDocuments === true,
        });
      } catch (error) {
        this.error = getErrorMessage(error);
      }
    },

    ensureDocumentColumns(existingDocuments = this.documents) {
      const nextDocuments = emptyBoard(this.columns);

      this.columns.forEach((column) => {
        nextDocuments[column.key] = existingDocuments[column.key] || [];
      });

      this.documents = nextDocuments;
    },

    cancelBoardTitleEdit() {
      this.editableBoardTitle = this.currentBoardTitle;
      this.$refs.boardTitleHeading.innerText = this.currentBoardTitle;
    },

    async saveBoardTitle(event) {
      if (!this.ensureBoardEditable()) {
        this.syncBoardTitleHeading();
        return;
      }

      const rawTitle = event && event.target ? event.target.innerText : this.editableBoardTitle;
      const title = rawTitle.trim() || 'Untitled board';

      this.error = '';

      try {
        if (this.currentBoardId) {
          const board = await boardsService.update(this.currentBoardId, {
            title,
            columns: this.columns,
            includeLegacyDocuments: this.currentBoardIncludesLegacyDocuments,
          });
          this.rememberCurrentBoard(board);
        } else {
          const board = await boardsService.create({
            title,
            text: 'Created from the document board.',
            status: 'active',
            type: 'board',
            columns: this.columns,
            includeLegacyDocuments: this.currentBoardIncludesLegacyDocuments,
          });
          this.rememberCurrentBoard(board);
        }
      } catch (error) {
        this.error = getErrorMessage(error);
        this.$refs.boardTitleHeading.innerText = this.currentBoardTitle;
      }
    },

    async loadDocuments() {
      this.isLoading = true;
      this.error = '';

      try {
        const records = await documentsService.list();
        const grouped = emptyBoard(this.columns);
        const fallbackStatus = this.columns[0].key;

        records
          .filter((record) => {
            if (!this.currentBoardId) {
              return !record.boardId;
            }

            return (
              record.boardId === this.currentBoardId ||
              (!record.boardId && this.currentBoardIncludesLegacyDocuments)
            );
          })
          .forEach((record) => {
            const status = grouped[record.status] ? record.status : fallbackStatus;
            grouped[status].push({ ...record, status });
          });

        this.documents = grouped;
      } catch (error) {
        this.error = getErrorMessage(error);
      } finally {
        this.isLoading = false;
      }
    },

    async createDocument() {
      if (!this.ensureBoardEditable()) {
        return;
      }

      const title = this.newDocumentTitle.trim();
      const text = this.newDocumentText.trim();

      if (!title && !text) {
        return;
      }

      this.isSaving = true;
      this.error = '';

      try {
        const firstColumnKey = this.columns[0].key;
        const document = await documentsService.create({
          title: title || 'Untitled document',
          text,
          status: firstColumnKey,
          type: 'document',
          boardId: this.currentBoardId || undefined,
        });

        this.documents[firstColumnKey].unshift(document);
        this.newDocumentTitle = '';
        this.newDocumentText = '';
        this.$refs.newDocumentEditor.innerHTML = '';
      } catch (error) {
        this.error = getErrorMessage(error);
      } finally {
        this.isSaving = false;
      }
    },

    async addColumn() {
      if (!this.ensureBoardEditable()) {
        return;
      }

      const title = `Column ${this.columns.length + 1}`;
      const column = {
        key: createColumnKey(),
        title,
      };

      this.columns.push(column);
      this.$set(this.documents, column.key, []);
      await this.persistBoardColumns();
    },

    async removeColumn(column) {
      if (!this.ensureBoardEditable()) {
        return;
      }

      if (this.columns.length <= 1) {
        return;
      }

      const movedDocuments = this.documents[column.key] || [];
      const message = movedDocuments.length
        ? `Delete the "${column.title}" column? Its ${movedDocuments.length} document(s) will move to another column.`
        : `Delete the "${column.title}" column?`;
      const confirmed = window.confirm(message);

      if (!confirmed) {
        return;
      }

      const fallbackColumn = this.columns.find((item) => item.key !== column.key);

      this.columns = this.columns.filter((item) => item.key !== column.key);
      this.ensureDocumentColumns();
      this.documents[fallbackColumn.key] = [
        ...movedDocuments.map((document) => ({
          ...document,
          status: fallbackColumn.key,
        })),
        ...(this.documents[fallbackColumn.key] || []),
      ];

      await this.persistBoardColumns();
      this.persistStatuses();
    },

    async saveColumnTitle(column, event) {
      if (!this.ensureBoardEditable()) {
        event.target.innerText = column.title;
        return;
      }

      const title = event.target.innerText.trim() || 'Untitled';
      column.title = title;
      event.target.innerText = title;
      await this.persistBoardColumns();
    },

    resetColumnTitle(column, event) {
      event.target.innerText = column.title;
      event.target.blur();
    },

    async createBoard() {
      if (!this.isSignedIn) {
        this.error = 'Sign in to create and save new boards.';
        return;
      }

      this.isCreatingBoard = true;
      this.error = '';

      try {
        const columns = cloneDefaultColumns();
        const board = await boardsService.create({
          title: 'Document Board',
          text: 'Created from the document board.',
          status: 'active',
          type: 'board',
          columns,
          includeLegacyDocuments: false,
        });
        this.rememberCurrentBoard(board, { includeLegacyDocuments: false });
        this.columns = columns;
        this.documents = emptyBoard(this.columns);
        this.newDocumentTitle = '';
        this.newDocumentText = '';
        await this.persistBoardColumns();

        if (this.$refs.newDocumentEditor) {
          this.$refs.newDocumentEditor.innerHTML = '';
        }

        if (this.$route.query.boardId !== String(board._id)) {
          await this.$router.replace({ name: 'home', query: { boardId: String(board._id) } });
        }
      } catch (error) {
        this.error = getErrorMessage(error);
      } finally {
        this.isCreatingBoard = false;
      }
    },

    async persistStatuses() {
      if (!this.ensureBoardEditable()) {
        return;
      }

      const updates = [];

      Object.entries(this.documents).forEach(([status, documents]) => {
        documents.forEach((document) => {
          if (document._id && document.status !== status) {
            this.$set(document, 'status', status);
            updates.push(documentsService.update(document._id, { status }));
          }
        });
      });

      if (!updates.length) {
        return;
      }

      try {
        await Promise.all(updates);
      } catch (error) {
        this.error = getErrorMessage(error);
      }
    },

    async updateDocument(document) {
      if (!this.ensureBoardEditable()) {
        return;
      }

      if (!document._id) {
        return;
      }

      try {
        await documentsService.update(document._id, {
          title: document.title,
          text: document.text,
          status: document.status,
        });
      } catch (error) {
        this.error = getErrorMessage(error);
      }
    },

    formatNewDocument(command) {
      if (!this.currentBoardCanEdit) {
        return;
      }

      this.$refs.newDocumentEditor.focus();
      window.document.execCommand(command, false, null);
      this.syncNewDocumentText();
    },

    syncNewDocumentText() {
      this.newDocumentText = this.$refs.newDocumentEditor.innerHTML;
    },

    openDocumentModal(document) {
      this.selectedDocument = document;
      this.modalDocumentTitle = document.title || '';
      this.modalDocumentText = plainTextToHtml(document.text || '');
      this.isDocumentModalOpen = true;

      this.$nextTick(() => {
        this.$refs.modalDocumentEditor.innerHTML = this.modalDocumentText;
        if (this.currentBoardCanEdit) {
          this.$refs.modalDocumentEditor.focus();
        }
      });
    },

    resetDocumentModal() {
      this.selectedDocument = null;
      this.modalDocumentTitle = '';
      this.modalDocumentText = '';
    },

    formatModalDocument(command) {
      if (!this.currentBoardCanEdit) {
        return;
      }

      this.$refs.modalDocumentEditor.focus();
      window.document.execCommand(command, false, null);
      this.syncModalDocumentText();
    },

    syncModalDocumentText() {
      this.modalDocumentText = this.$refs.modalDocumentEditor.innerHTML;
    },

    async saveDocumentModal() {
      if (!this.ensureBoardEditable()) {
        return;
      }

      if (!this.selectedDocument) {
        return;
      }

      this.isModalSaving = true;
      this.error = '';
      this.syncModalDocumentText();

      try {
        this.selectedDocument.title = this.modalDocumentTitle.trim() || 'Untitled document';
        this.selectedDocument.text = this.modalDocumentText;
        await this.updateDocument(this.selectedDocument);
        this.isDocumentModalOpen = false;
      } catch (error) {
        this.error = getErrorMessage(error);
      } finally {
        this.isModalSaving = false;
      }
    },

    async deleteSelectedDocument() {
      if (!this.ensureBoardEditable()) {
        return;
      }

      if (!this.selectedDocument) {
        return;
      }

      await this.deleteDocument(this.selectedDocument);
      this.isDocumentModalOpen = false;
    },

    async deleteDocument(document) {
      if (!this.ensureBoardEditable()) {
        return;
      }

      if (!document._id) {
        return;
      }

      try {
        await documentsService.remove(document._id);
        Object.keys(this.documents).forEach((status) => {
          this.documents[status] = this.documents[status].filter(
            (item) => item._id !== document._id
          );
        });
      } catch (error) {
        this.error = getErrorMessage(error);
      }
    },
  },
};
</script>

<style scoped>
.home {
  text-align: left;
}

.board-header {
  align-items: flex-start;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  margin-bottom: 24px;
}

.board-title {
  background: transparent;
  border: 0;
  border-bottom: 1px solid transparent;
  cursor: text;
  font-size: 32px;
  font-weight: 700;
  margin: 0;
  outline: none;
  padding: 0 0 2px;
}

.board-title:hover,
.board-title:focus {
  border-bottom-color: rgba(66, 185, 131, 0.45);
}

.board-title.is-read-only {
  cursor: default;
}

.board-title.is-read-only:hover,
.board-title.is-read-only:focus,
.column-title.is-read-only:hover,
.column-title.is-read-only:focus {
  border-bottom-color: transparent;
}

.board-header-actions {
  display: flex;
  gap: 8px;
}

.compose-panel {
  display: grid;
  gap: 12px;
  grid-template-columns: minmax(180px, 260px) minmax(320px, 1fr) auto;
  margin-bottom: 24px;
}

.compose-panel.is-read-only {
  opacity: 0.72;
}

.btn-green,
.btn-green:disabled {
  background: #42b983;
  border-color: #42b983;
  color: #ffffff;
}

.btn-green:hover,
.btn-green:focus {
  background: #359b6d;
  border-color: #359b6d;
  color: #ffffff;
}

.add-card-primary {
  box-shadow: 0 2px 6px rgba(47, 158, 116, 0.18);
}

.btn-green-outline {
  background: #ffffff;
  border-color: #42b983;
  color: #42b983;
}

.btn-green-outline:hover,
.btn-green-outline:focus {
  background: #42b983;
  border-color: #42b983;
  color: #ffffff;
}

.editor-shell {
  background: #ffffff;
  border: 1px solid #ced4da;
  border-radius: 4px;
  min-width: 0;
}

.editor-shell:focus-within {
  border-color: #42b983;
  box-shadow: 0 0 0 0.2rem rgba(66, 185, 131, 0.18);
}

.editor-shell.compact {
  border-color: #dfe3e8;
}

.editor-toolbar {
  align-items: center;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  gap: 4px;
  padding: 6px;
}

.editor-toolbar button {
  align-items: center;
  background: #ffffff;
  border: 1px solid transparent;
  border-radius: 4px;
  color: #42b983;
  display: inline-flex;
  font-size: 14px;
  height: 28px;
  justify-content: center;
  line-height: 1;
  min-width: 28px;
  padding: 0 8px;
}

.editor-toolbar button:hover,
.editor-toolbar button:focus {
  background: rgba(66, 185, 131, 0.1);
  border-color: rgba(66, 185, 131, 0.25);
  outline: none;
}

.editor-toolbar button:disabled {
  color: #8a9690;
  cursor: not-allowed;
}

.rich-editor {
  color: #2c3e50;
  font-size: 15px;
  min-height: 85px;
  outline: none;
  overflow-wrap: anywhere;
  padding: 10px 12px;
}

.rich-editor:empty::before {
  color: #6c757d;
  content: attr(data-placeholder);
}

.rich-editor p,
.rich-editor ul,
.rich-editor ol {
  margin-bottom: 8px;
}

.document-modal-body {
  display: grid;
  gap: 12px;
}

.modal-label {
  color: #2c3e50;
  font-size: 13px;
  font-weight: 700;
  margin: 0;
}

.modal-title-input {
  font-weight: 700;
}

.modal-editor-shell {
  min-height: 280px;
}

.modal-rich-editor {
  min-height: 260px;
}

.modal-actions {
  align-items: center;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  margin-top: 8px;
}

.modal-actions-right {
  display: flex;
  gap: 8px;
}

.kanban-bleed {
  margin-left: calc(50% - 50vw);
  overflow-x: auto;
  padding: 0 16px 4px 0;
  width: 100vw;
}

.kanban-board {
  align-items: start;
  display: grid;
  gap: 16px;
  grid-auto-columns: 320px;
  grid-auto-flow: column;
  justify-content: start;
  min-width: max-content;
  padding: 0 0 4px calc(max((100vw - 1140px) / 2, 0px) + 15px);
  scrollbar-gutter: stable both-edges;
}

.kanban-column {
  background: #f7f8fa;
  border: 1px solid #dfe3e8;
  border-radius: 6px;
  min-height: 420px;
  padding: 12px;
  width: 320px;
}

.kanban-column-header {
  align-items: center;
  display: flex;
  gap: 8px;
  justify-content: space-between;
  margin-bottom: 12px;
}

.column-drag-handle {
  align-items: center;
  background: #ffffff;
  border: 1px solid #dfe3e8;
  border-radius: 4px;
  color: #6c757d;
  cursor: grab;
  display: inline-flex;
  flex: 0 0 auto;
  font-size: 14px;
  height: 26px;
  justify-content: center;
  line-height: 1;
  padding: 0;
  width: 26px;
}

.column-drag-handle:active {
  cursor: grabbing;
}

.column-drag-handle:hover,
.column-drag-handle:focus {
  border-color: #42b983;
  color: #42b983;
  outline: none;
}

.column-drag-handle:disabled {
  cursor: default;
  opacity: 0.55;
}

.column-title {
  border-bottom: 1px solid transparent;
  font-size: 16px;
  font-weight: 700;
  margin: 0;
  min-width: 0;
  outline: none;
  padding-bottom: 1px;
}

.column-title:hover,
.column-title:focus {
  border-bottom-color: rgba(66, 185, 131, 0.45);
}

.column-controls {
  align-items: center;
  display: flex;
  gap: 6px;
}

.column-controls span {
  background: #ffffff;
  border: 1px solid #dfe3e8;
  border-radius: 999px;
  color: #495057;
  font-size: 13px;
  min-width: 28px;
  padding: 2px 8px;
  text-align: center;
}

.column-remove {
  align-items: center;
  background: #ffffff;
  border: 1px solid #dfe3e8;
  border-radius: 999px;
  color: #6c757d;
  display: inline-flex;
  font-size: 16px;
  height: 24px;
  justify-content: center;
  line-height: 1;
  padding: 0;
  width: 24px;
}

.column-remove:hover,
.column-remove:focus {
  border-color: #dc3545;
  color: #dc3545;
  outline: none;
}

.kanban-list {
  display: grid;
  gap: 12px;
  min-height: 340px;
}

.document-card {
  background: #ffffff;
  border: 1px solid #dfe3e8;
  border-radius: 6px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.06);
  display: grid;
  gap: 10px;
  padding: 12px;
  text-align: left;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
}

.document-card:hover,
.document-card:focus {
  border-color: rgba(66, 185, 131, 0.55);
  box-shadow: 0 6px 16px rgba(16, 24, 40, 0.08);
  outline: none;
  transform: translateY(-1px);
}

.document-title {
  color: #2c3e50;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.35;
  margin: 0;
}

@media (max-width: 991px) {
  .compose-panel {
    grid-template-columns: 1fr;
  }

  .kanban-bleed {
    margin-left: calc(50% - 50vw);
    padding-right: 12px;
    width: 100vw;
  }

  .kanban-board {
    grid-auto-columns: minmax(280px, 280px);
    padding-left: 12px;
  }

  .kanban-column {
    width: 280px;
  }
}
</style>
